import type { ColorSchemeType, HslColorFilter } from "./types";

export const normalizeHue = (hue: number) => ((hue % 360) + 360) % 360;

export function colorFromPickerPoint(
  point: { x: number; y: number },
  rect: { left: number; top: number; width: number; height: number },
) {
  const dx = point.x - (rect.left + rect.width / 2);
  const dy = point.y - (rect.top + rect.height / 2);
  const radius = Math.min(1, Math.hypot(dx, dy) / (Math.min(rect.width, rect.height) / 2));

  return {
    hue: Math.round(normalizeHue(Math.atan2(dy, dx) * 180 / Math.PI)),
    saturation: Math.round(radius * 100),
  };
}

export function hslToRgb({ hue, saturation, lightness }: HslColorFilter): [number, number, number] {
  const h = normalizeHue(hue);
  const s = Math.max(0, Math.min(100, saturation)) / 100;
  const l = Math.max(0, Math.min(100, lightness)) / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const x = chroma * (1 - Math.abs((h / 60) % 2 - 1));
  const offset = l - chroma / 2;
  const [r, g, b] = h < 60 ? [chroma, x, 0]
    : h < 120 ? [x, chroma, 0]
      : h < 180 ? [0, chroma, x]
        : h < 240 ? [0, x, chroma]
          : h < 300 ? [x, 0, chroma]
            : [chroma, 0, x];
  return [r, g, b].map((channel) => Math.round((channel + offset) * 255)) as [number, number, number];
}

export function hslToHex(color: HslColorFilter): string {
  return `#${hslToRgb(color).map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}

export function hexToHslColor(hex: string): HslColorFilter {
  const value = hex.replace("#", "");
  const expanded = value.length === 3 ? [...value].map((channel) => channel + channel).join("") : value;
  const rgb = Number.parseInt(expanded, 16);
  const red = ((rgb >> 16) & 255) / 255;
  const green = ((rgb >> 8) & 255) / 255;
  const blue = (rgb & 255) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const lightness = (max + min) / 2;
  let hue = 0;
  if (delta !== 0) {
    if (max === red) hue = 60 * (((green - blue) / delta) % 6);
    else if (max === green) hue = 60 * ((blue - red) / delta + 2);
    else hue = 60 * ((red - green) / delta + 4);
  }
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  return {
    hue: normalizeHue(hue),
    saturation: saturation * 100,
    lightness: lightness * 100,
  };
}

const COLOR_SCHEME_OFFSETS: Record<ColorSchemeType, number[]> = {
  analogous: [-30, 0, 30],
  complementary: [0, 180],
  "split-complement": [0, 150, 210],
  triadic: [0, 120, 240],
  tetradic: [0, 90, 180, 270],
};

export function generateColorScheme(color: HslColorFilter, type: ColorSchemeType): HslColorFilter[] {
  return COLOR_SCHEME_OFFSETS[type].map((offset) => ({
    ...color,
    hue: normalizeHue(color.hue + offset),
  }));
}

export function appendUniqueColors(
  current: readonly HslColorFilter[],
  additions: readonly HslColorFilter[],
): HslColorFilter[] {
  const seen = new Set(current.map(hslToHex));
  const next = current.map((color) => ({ ...color }));
  additions.forEach((color) => {
    const hex = hslToHex(color);
    if (seen.has(hex)) return;
    seen.add(hex);
    next.push({ ...color });
  });
  return next;
}

export function hslToLab(color: HslColorFilter): [number, number, number] {
  const [red, green, blue] = hslToRgb(color).map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const x = (red * 0.4124564 + green * 0.3575761 + blue * 0.1804375) / 0.95047;
  const y = red * 0.2126729 + green * 0.7151522 + blue * 0.072175;
  const z = (red * 0.0193339 + green * 0.119192 + blue * 0.9503041) / 1.08883;
  const transform = (value: number) => value > 216 / 24389
    ? Math.cbrt(value)
    : (24389 / 27 * value + 16) / 116;
  const fx = transform(x);
  const fy = transform(y);
  const fz = transform(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/** CIEDE2000 color difference for D65 CIELAB values. */
export function deltaE2000(
  [l1, a1, b1]: readonly [number, number, number],
  [l2, a2, b2]: readonly [number, number, number],
): number {
  const degrees = (radians: number) => radians * 180 / Math.PI;
  const radians = (degreesValue: number) => degreesValue * Math.PI / 180;
  const c1 = Math.hypot(a1, b1);
  const c2 = Math.hypot(a2, b2);
  const meanC = (c1 + c2) / 2;
  const meanC7 = meanC ** 7;
  const g = 0.5 * (1 - Math.sqrt(meanC7 / (meanC7 + 25 ** 7)));
  const a1Prime = (1 + g) * a1;
  const a2Prime = (1 + g) * a2;
  const c1Prime = Math.hypot(a1Prime, b1);
  const c2Prime = Math.hypot(a2Prime, b2);
  const hue = (a: number, b: number) => normalizeHue(degrees(Math.atan2(b, a)));
  const h1Prime = hue(a1Prime, b1);
  const h2Prime = hue(a2Prime, b2);
  const deltaLPrime = l2 - l1;
  const deltaCPrime = c2Prime - c1Prime;
  const hueDifference = h2Prime - h1Prime;
  const deltaHuePrime = c1Prime * c2Prime === 0
    ? 0
    : Math.abs(hueDifference) <= 180
      ? hueDifference
      : hueDifference > 180 ? hueDifference - 360 : hueDifference + 360;
  const deltaHPrime = 2 * Math.sqrt(c1Prime * c2Prime) * Math.sin(radians(deltaHuePrime / 2));
  const meanLPrime = (l1 + l2) / 2;
  const meanCPrime = (c1Prime + c2Prime) / 2;
  const meanHuePrime = c1Prime * c2Prime === 0
    ? h1Prime + h2Prime
    : Math.abs(h1Prime - h2Prime) <= 180
      ? (h1Prime + h2Prime) / 2
      : h1Prime + h2Prime < 360
        ? (h1Prime + h2Prime + 360) / 2
        : (h1Prime + h2Prime - 360) / 2;
  const t = 1
    - 0.17 * Math.cos(radians(meanHuePrime - 30))
    + 0.24 * Math.cos(radians(2 * meanHuePrime))
    + 0.32 * Math.cos(radians(3 * meanHuePrime + 6))
    - 0.20 * Math.cos(radians(4 * meanHuePrime - 63));
  const deltaTheta = 30 * Math.exp(-(((meanHuePrime - 275) / 25) ** 2));
  const meanCPrime7 = meanCPrime ** 7;
  const rc = 2 * Math.sqrt(meanCPrime7 / (meanCPrime7 + 25 ** 7));
  const lightnessOffset = meanLPrime - 50;
  const sl = 1 + 0.015 * lightnessOffset ** 2 / Math.sqrt(20 + lightnessOffset ** 2);
  const sc = 1 + 0.045 * meanCPrime;
  const sh = 1 + 0.015 * meanCPrime * t;
  const rt = -Math.sin(radians(2 * deltaTheta)) * rc;
  const lightnessTerm = deltaLPrime / sl;
  const chromaTerm = deltaCPrime / sc;
  const hueTerm = deltaHPrime / sh;
  return Math.sqrt(
    lightnessTerm ** 2
    + chromaTerm ** 2
    + hueTerm ** 2
    + rt * chromaTerm * hueTerm,
  );
}
